import { InventoryListing } from "../../../models/inventory/InventoryListing";
import { InventoryFilters } from "../../../models/inventory/InventoryListingFilters";
import { InventorySwagpackVariant } from "../../../models/inventory/InventorySwagpackVariant";
import {
  InventoryAdjustCompleteRequest,
  InventoryAdjustCompleteResult,
  InventoryAdjustImportCsvResult,
  InventoryAdjustRequest,
  InventoryAdjustResult,
  InventoryBuildableResponse,
  InventoryContributionFilters,
  InventoryContributionsResponse,
  InventoryCountImportLogsFilters,
  InventoryCountImportLogsResponse,
  InventoryDirectDispatchRequest,
  InventoryInboundImportCsvResult,
  InventoryInboundImportLogsFilters,
  InventoryInboundImportLogsResponse,
  InventoryInboundRequest,
  InventorySkuImportCsvResult,
  InventoryMovementResult,
  InventoryMovementsFilters,
  InventoryMovementsResponse,
  InventoryOrderPacksResponse,
  InventoryOrderSkusResponse,
  InventoryPack,
  InventoryPackCreateRequest,
  InventoryPackFilters,
  InventoryPackReplaceItemsRequest,
  InventoryPackReplaceItemsResult,
  InventoryPacksResponse,
  InventoryPackSummary,
  InventoryPackUpdateRequest,
  InventoryStockFilters,
  InventoryStockResponse,
  InventoryVariant,
  InventoryVariantCreateRequest,
  InventoryVariantFilters,
  InventoryVariantSoftDeleteResult,
  InventoryVariantsResponse,
  InventoryVariantUpdateRequest,
} from "../../../models/inventory/InventoryAdmin";
import {
  APIResponse,
  APIBlobResponse,
  PaginatedAPIResponse,
} from "../../../models/NetworkResponse";
import {
  getBlobAPI,
  getAPI,
  postAPI,
  patchAPI,
  putAPI,
  deleteAPI,
  postMultipartAPI,
} from "../../../api/Networking";

const ADMIN_BASE = "/v1/inventory/admin";

export default class InventoryAPI {
  getIds = async (searchText: string): Promise<number[]> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("search_text", searchText);

      const response = await getAPI(
        `/v5/inventories/ids?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderInventoryIdByOrderId = async (
    orderId: number,
  ): Promise<number | null> => {
    try {
      const response = await getAPI(
        `/v5/inventories/order/${orderId}/inventory-id`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getMasterInventoryIdByCustomerId = async (
    customerId: number,
  ): Promise<number | null> => {
    try {
      const response = await getAPI(
        `/v5/inventories/customer/${customerId}/inventory-id`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getListing = async (
    filters: InventoryFilters,
  ): Promise<PaginatedAPIResponse<InventoryListing> | APIBlobResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page_no)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit) queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text.toString());

      if (filters.order_id)
        queryParams.append("order_id", filters.order_id.toString());

      if (filters.customer_id)
        queryParams.append("customer_id", filters.customer_id.toString());

      if (filters.inventory_id)
        queryParams.append("inventory_id", filters.inventory_id.toString());

      if (
        filters.inventory_type !== null &&
        filters.inventory_type !== undefined
      ) {
        queryParams.append("inventory_type", filters.inventory_type.toString());
      }

      if (filters.sort_by)
        queryParams.append("sort_by", filters.sort_by.toString());

      if (filters.sort_type)
        queryParams.append("sort_type", filters.sort_type.toString());
      const response = await getAPI(
        `/v5/inventories?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getSwagpackVariants = async (
    packId: number,
  ): Promise<
    PaginatedAPIResponse<InventorySwagpackVariant> | APIBlobResponse
  > => {
    try {
      const response = await getAPI(
        `/v5/inventories/inventory/packs/${packId}/variants`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  downloadCountReportCsv = async (
    inventoryId: number,
  ): Promise<APIBlobResponse> => {
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      throw new Error("Invalid inventory ID");
    }

    try {
      const blob = await getBlobAPI(
        `/v1/inventory/${inventoryId}/count-report/csv`,
      );

      if (!blob) {
        throw new Error("Count report download failed");
      }

      return {
        data: blob,
        filename: `inventory-${inventoryId}-count-report.csv`,
      };
    } catch (error) {
      throw error;
    }
  };

  uploadSwagpackQuantityCsv = async (
    inventoryId: number,
    file: File,
  ): Promise<APIResponse> => {
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      throw new Error("Invalid inventory ID");
    }

    try {
      const formData = new FormData();
      formData.append("csv_file", file);

      return await postMultipartAPI(
        `/v1/inventory/${inventoryId}/swagpack/quantity/import-csv`,
        formData,
      );
    } catch (error) {
      throw error;
    }
  };

  // ── Admin v2: Movements ───────────────────────────────────────────────────

  inbound = async (
    payload: InventoryInboundRequest,
  ): Promise<InventoryMovementResult> => {
    try {
      if (!payload.orderId && !payload.inventoryId) {
        throw new Error("Inbound requires orderId or inventoryId");
      }
      const response = await postAPI(`${ADMIN_BASE}/inbound`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  directDispatch = async (
    payload: InventoryDirectDispatchRequest,
  ): Promise<InventoryMovementResult> => {
    try {
      const response = await postAPI(`${ADMIN_BASE}/direct-dispatch`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  adjust = async (
    payload: InventoryAdjustRequest,
  ): Promise<InventoryAdjustResult> => {
    try {
      const response = await postAPI(`${ADMIN_BASE}/adjust`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  adjustComplete = async (
    payload: InventoryAdjustCompleteRequest,
  ): Promise<InventoryAdjustCompleteResult> => {
    try {
      const response = await postAPI(`${ADMIN_BASE}/adjust/complete`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  adjustImportCsv = async (
    inventoryId: number,
    note: string,
    csvFile: File,
  ): Promise<InventoryAdjustImportCsvResult> => {
    try {
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        throw new Error("Invalid inventory ID");
      }
      if (!note.trim()) {
        throw new Error("Adjustment note is required");
      }
      if (!csvFile) {
        throw new Error("CSV file is required");
      }

      const formData = new FormData();
      formData.append("inventoryId", String(inventoryId));
      formData.append("note", note.trim());
      formData.append("csv_file", csvFile);

      const response = await postMultipartAPI(
        `${ADMIN_BASE}/adjust/import-csv`,
        formData,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  inboundImportCsv = async (
    inventoryId: number,
    note: string,
    csvFile: File,
  ): Promise<InventoryInboundImportCsvResult> => {
    try {
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        throw new Error("Invalid inventory ID");
      }
      if (!note.trim()) {
        throw new Error("Inbound note is required");
      }
      if (!csvFile) {
        throw new Error("CSV file is required");
      }

      const formData = new FormData();
      formData.append("inventoryId", String(inventoryId));
      formData.append("note", note.trim());
      formData.append("csv_file", csvFile);

      const response = await postMultipartAPI(
        `${ADMIN_BASE}/inbound/import-csv`,
        formData,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  importSkuCsv = async (
    inventoryId: number,
    note: string,
    csvFile: File,
  ): Promise<InventorySkuImportCsvResult> => {
    try {
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        throw new Error("Invalid inventory ID");
      }
      if (!note.trim()) {
        throw new Error("SKU update note is required");
      }
      if (!csvFile) {
        throw new Error("CSV file is required");
      }

      const formData = new FormData();
      formData.append("inventoryId", String(inventoryId));
      formData.append("note", note.trim());
      formData.append("csv_file", csvFile);

      const response = await postMultipartAPI(
        `${ADMIN_BASE}/variants/import-sku-csv`,
        formData,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  // ── Admin v2: Reads ───────────────────────────────────────────────────────

  getStock = async (
    filters: InventoryStockFilters,
  ): Promise<InventoryStockResponse> => {
    try {
      const params: Record<string, string | number | boolean> = {
        inventoryId: filters.inventoryId,
      };
      if (filters.productId != null) params.productId = filters.productId;
      if (filters.includeInactive != null)
        params.includeInactive = filters.includeInactive;
      if (filters.search) params.search = filters.search;

      const response = await getAPI(`${ADMIN_BASE}/stock`, params);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getContributions = async (
    filters: InventoryContributionFilters,
  ): Promise<InventoryContributionsResponse> => {
    try {
      const params: Record<string, string | number> = {
        inventoryId: filters.inventoryId,
      };
      if (filters.clientSku) params.clientSku = filters.clientSku;
      if (filters.orderId != null) params.orderId = filters.orderId;

      const response = await getAPI(`${ADMIN_BASE}/contributions`, params);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderSkus = async (
    orderId: number,
  ): Promise<InventoryOrderSkusResponse> => {
    try {
      const response = await getAPI(`${ADMIN_BASE}/orders/${orderId}/skus`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderPacks = async (
    orderId: number,
  ): Promise<InventoryOrderPacksResponse> => {
    try {
      const response = await getAPI(`${ADMIN_BASE}/orders/${orderId}/packs`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getMovements = async (
    filters: InventoryMovementsFilters,
  ): Promise<InventoryMovementsResponse> => {
    try {
      const params: Record<string, string | number> = {
        inventoryId: filters.inventoryId,
      };
      if (filters.clientSku) params.clientSku = filters.clientSku;
      if (filters.refType) params.refType = filters.refType;
      if (filters.refId != null) params.refId = filters.refId;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.limit != null) params.limit = filters.limit;
      if (filters.cursor != null) params.cursor = filters.cursor;

      const response = await getAPI(`${ADMIN_BASE}/movements`, params);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getBuildable = async (
    inventoryId: number,
  ): Promise<InventoryBuildableResponse> => {
    try {
      const response = await getAPI(`${ADMIN_BASE}/buildable`, {
        inventoryId,
      });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  // ── Admin v2: Variants ────────────────────────────────────────────────────

  createVariant = async (
    payload: InventoryVariantCreateRequest,
  ): Promise<InventoryVariant> => {
    try {
      const response = await postAPI(`${ADMIN_BASE}/variants`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getVariants = async (
    filters: InventoryVariantFilters,
  ): Promise<InventoryVariantsResponse> => {
    try {
      const params: Record<string, string | number | boolean> = {};
      if (filters.inventoryId != null) params.inventoryId = filters.inventoryId;
      if (filters.productId != null) params.productId = filters.productId;
      if (filters.includeInactive != null)
        params.includeInactive = filters.includeInactive;
      if (filters.search) params.search = filters.search;
      if (filters.tagId) params.tagId = filters.tagId;
      if (filters.page_no != null) params.page_no = filters.page_no;
      if (filters.limit != null) params.limit = filters.limit;
      if (filters.sortBy) params.sort_by = filters.sortBy;
      if (filters.sortType) params.sort_type = filters.sortType;

      const response = await getAPI(`${ADMIN_BASE}/variants`, params);
      const result = response.result as InventoryVariantsResponse;
      const variants = (result?.variants ?? []).map((variant) => {
        const raw = variant as InventoryVariant & {
          created_date?: string | null;
          modified_date?: string | null;
        };
        return {
          ...variant,
          createdAt: variant.createdAt ?? raw.created_date ?? null,
          modifiedAt: variant.modifiedAt ?? raw.modified_date ?? null,
        };
      });
      return {
        ...result,
        variants,
      };
    } catch (error) {
      throw error;
    }
  };

  getVariantById = async (id: number): Promise<InventoryVariant> => {
    try {
      const response = await getAPI(`${ADMIN_BASE}/variants/${id}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getVariantBySku = async (
    inventoryId: number,
    clientSku: string,
  ): Promise<InventoryVariant> => {
    try {
      const response = await getAPI(`${ADMIN_BASE}/variants/by-sku`, {
        inventoryId,
        clientSku,
      });
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  updateVariant = async (
    id: number,
    payload: InventoryVariantUpdateRequest,
  ): Promise<InventoryVariant> => {
    try {
      const response = await patchAPI(`${ADMIN_BASE}/variants/${id}`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteVariant = async (
    id: number,
  ): Promise<InventoryVariantSoftDeleteResult> => {
    try {
      const response = await deleteAPI(`${ADMIN_BASE}/variants/${id}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  deleteVariantBySku = async (
    inventoryId: number,
    clientSku: string,
  ): Promise<InventoryVariantSoftDeleteResult> => {
    try {
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        throw new Error("Invalid inventory ID");
      }
      const trimmedSku = clientSku.trim();
      if (!trimmedSku) {
        throw new Error("Client SKU is required");
      }

      const params = new URLSearchParams();
      params.append("inventoryId", String(inventoryId));
      params.append("clientSku", trimmedSku);

      const response = await deleteAPI(
        `${ADMIN_BASE}/variants/by-sku?${params.toString()}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  reactivateVariant = async (id: number): Promise<InventoryVariant> => {
    try {
      const response = await postAPI(`${ADMIN_BASE}/variants/${id}/reactivate`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  // ── Admin v2: Packs ───────────────────────────────────────────────────────

  createPack = async (
    payload: InventoryPackCreateRequest,
  ): Promise<InventoryPack> => {
    try {
      const response = await postAPI(`${ADMIN_BASE}/packs`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  listPacks = async (
    filters: InventoryPackFilters,
  ): Promise<InventoryPacksResponse> => {
    try {
      const params: Record<string, string | number | boolean> = {
        inventoryId: filters.inventoryId,
      };
      if (filters.includeInactive != null)
        params.includeInactive = filters.includeInactive;
      if (filters.search) params.search = filters.search;
      if (filters.page_no != null) params.page_no = filters.page_no;
      if (filters.limit != null) params.limit = filters.limit;
      if (filters.sortBy) params.sort_by = filters.sortBy;
      if (filters.sortType) params.sort_type = filters.sortType;

      const response = await getAPI(`${ADMIN_BASE}/packs`, params);
      const result = response.result as InventoryPacksResponse;
      const packs = (result?.packs ?? []).map((pack) => {
        const raw = pack as InventoryPackSummary & {
          created_date?: string | null;
          modified_date?: string | null;
        };
        return {
          ...pack,
          createdAt: pack.createdAt ?? raw.created_date ?? null,
          modifiedAt: pack.modifiedAt ?? raw.modified_date ?? null,
        };
      });
      return {
        ...result,
        packs,
      };
    } catch (error) {
      throw error;
    }
  };

  getPack = async (packId: number): Promise<InventoryPack> => {
    try {
      const response = await getAPI(`${ADMIN_BASE}/packs/${packId}`);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  patchPack = async (
    packId: number,
    payload: InventoryPackUpdateRequest,
  ): Promise<InventoryPack> => {
    try {
      const response = await patchAPI(`${ADMIN_BASE}/packs/${packId}`, payload);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  replaceBom = async (
    packId: number,
    payload: InventoryPackReplaceItemsRequest,
  ): Promise<InventoryPackReplaceItemsResult> => {
    try {
      const response = await putAPI(
        `${ADMIN_BASE}/packs/${packId}/items`,
        payload,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getCountImportLogs = async (
    filters: InventoryCountImportLogsFilters,
  ): Promise<InventoryCountImportLogsResponse> => {
    try {
      const params: Record<string, string | number> = {
        page_no: filters.page_no ?? 1,
        limit: Math.min(filters.limit ?? 20, 100),
      };
      if (filters.search?.trim()) params.search = filters.search.trim();
      if (filters.inventoryId != null) params.inventoryId = filters.inventoryId;
      if (filters.customerId != null) params.customerId = filters.customerId;
      if (filters.adminId != null) params.adminId = filters.adminId;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await getAPI(`${ADMIN_BASE}/count-import-logs`, params);
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getInboundImportLogs = async (
    filters: InventoryInboundImportLogsFilters,
  ): Promise<InventoryInboundImportLogsResponse> => {
    try {
      const params: Record<string, string | number> = {
        page_no: filters.page_no ?? 1,
        limit: Math.min(filters.limit ?? 20, 100),
      };
      if (filters.search?.trim()) params.search = filters.search.trim();
      if (filters.inventoryId != null) params.inventoryId = filters.inventoryId;
      if (filters.customerId != null) params.customerId = filters.customerId;
      if (filters.adminId != null) params.adminId = filters.adminId;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await getAPI(
        `${ADMIN_BASE}/inbound-import-logs`,
        params,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };
}
